/**
 * useConsultationRecovery Hook
 *
 * Recovers consultation data (audio, summary) from IndexedDB
 * when a consultation page loads. This enables:
 * - Audio playback after page refresh
 * - Summary display after page refresh
 *
 * Usage:
 * ```tsx
 * const { audio, summary, isLoading, error } = useConsultationRecovery(consultationId);
 * ```
 */

import { useState, useEffect, useCallback } from 'react';
import {
  loadCompleteConsultation,
  hasPersistedData,
  type CompleteConsultationData,
} from '@/lib/stores/consultationPersistence';
import { useAudioStore } from '@/lib/stores/audioStore';
import { createComponentLogger } from '@/lib/utils/logger';

const logger = createComponentLogger('useConsultationRecovery');

/**
 * Recovery state interface
 */
export interface ConsultationRecoveryState {
  /** Whether recovery is in progress */
  isLoading: boolean;
  /** Error during recovery */
  error: string | null;
  /** Recovered audio data */
  audio: {
    blob: Blob;
    url: string;
    durationSeconds: number;
  } | null;
  /** Recovered summary data */
  summary: unknown | null;
  /** Summary file path if available */
  summaryFile: string | null;
  /** Whether any data was recovered */
  hasRecoveredData: boolean;
  /** Trigger manual recovery */
  recover: () => Promise<void>;
}

/**
 * Hook options
 */
export interface UseConsultationRecoveryOptions {
  /** Whether to auto-recover on mount (default: true) */
  autoRecover?: boolean;
  /** Whether to populate audio store with recovered audio (default: true) */
  populateAudioStore?: boolean;
}

/**
 * Hook to recover consultation data from IndexedDB
 */
export function useConsultationRecovery(
  consultationId: string,
  options: UseConsultationRecoveryOptions = {}
): ConsultationRecoveryState {
  const { autoRecover = true, populateAudioStore = true } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CompleteConsultationData | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Get audio store methods
  const loadAudioFromPersistence = useAudioStore((state) => state.loadAudioFromPersistence);
  const getAudioRecording = useAudioStore((state) => state.getAudioRecording);

  /**
   * Recover data from IndexedDB
   */
  const recover = useCallback(async () => {
    if (!consultationId) {
      logger.warn('No consultation ID provided for recovery');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      logger.log('Recovering consultation data...', { consultationId });

      // Check if data exists first (fast check)
      const dataStatus = await hasPersistedData(consultationId);

      if (!dataStatus.hasAudio && !dataStatus.hasSummary) {
        logger.log('No persisted data found for consultation', { consultationId });
        setIsLoading(false);
        return;
      }

      logger.log('Found persisted data:', dataStatus);

      // Load complete consultation data
      const consultationData = await loadCompleteConsultation(consultationId);
      setData(consultationData);

      // If audio exists, create URL and optionally populate audio store
      if (consultationData.audio) {
        const url = URL.createObjectURL(consultationData.audio.blob);
        setAudioUrl(url);

        if (populateAudioStore) {
          // Load into audio store for consistent access
          await loadAudioFromPersistence(consultationId);
        }

        logger.log('Audio recovered', {
          consultationId,
          size: consultationData.audio.blob.size,
          durationSeconds: consultationData.audio.durationSeconds,
        });
      }

      if (consultationData.summary) {
        logger.log('Summary recovered', { consultationId });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to recover consultation data';
      logger.error('Recovery failed:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [consultationId, populateAudioStore, loadAudioFromPersistence]);

  // Auto-recover on mount if enabled
  useEffect(() => {
    if (autoRecover && consultationId) {
      recover();
    }

    // Cleanup audio URL on unmount
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [consultationId, autoRecover]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if audio is in store (might have been loaded separately)
  const existingAudio = getAudioRecording(consultationId);

  return {
    isLoading,
    error,
    audio: data?.audio
      ? {
          blob: data.audio.blob,
          url: audioUrl || existingAudio?.audioUrl || '',
          durationSeconds: data.audio.durationSeconds,
        }
      : existingAudio
        ? {
            blob: existingAudio.audioBlob,
            url: existingAudio.audioUrl,
            durationSeconds: existingAudio.durationSeconds,
          }
        : null,
    // Handle both old format (full SessionInfoResponse) and new format (just SessionSummary)
    // Old format: { success: true, summary: {...}, summary_file: "..." }
    // New format: { chief_complaint: {...}, ... }
    summary: data?.summary ? (data.summary as { summary?: unknown }).summary || data.summary : null,
    summaryFile: data?.summaryFile || null,
    hasRecoveredData: !!(data?.audio || data?.summary),
    recover,
  };
}

/**
 * Simple hook to check if consultation has persisted data
 * Useful for conditional UI rendering
 */
export function useHasPersistedData(consultationId: string): {
  hasAudio: boolean;
  hasSummary: boolean;
  isChecking: boolean;
} {
  const [status, setStatus] = useState({
    hasAudio: false,
    hasSummary: false,
    isChecking: true,
  });

  useEffect(() => {
    if (!consultationId) {
      setStatus((prev) => ({ ...prev, isChecking: false }));
      return;
    }

    hasPersistedData(consultationId)
      .then((result) => {
        setStatus({
          ...result,
          isChecking: false,
        });
      })
      .catch((err) => {
        logger.error('Failed to check persisted data:', err);
        setStatus((prev) => ({ ...prev, isChecking: false }));
      });
  }, [consultationId]);

  return status;
}
